import { defineStore } from "pinia";
import { ref } from "vue";
import { authFetch } from "@/api/auth-fetch";
import type { UserBrand } from "@/api/models";

/**
 * Per-user brand allowlist management. Two flavours of read:
 *
 *   - `fetchMine()` — the calling user's own allowlist; powers the
 *     BrandSwitcher's option filter so the dropdown only shows
 *     brands the user can actually scope to.
 *   - `fetchForUser(id)` — the admin user-detail page's "Brändit"
 *     tab. Requires `user_brand:read`.
 *
 * Plus the admin mutations: setAllowlist (bulk replace), grant,
 * revoke. The setAllowlist path is what the GUI's checkbox editor
 * calls so the entire row set is replaced atomically.
 */
async function unwrapData<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as
      | { message?: string | string[] }
      | null;
    throw new Error(
      Array.isArray(err?.message)
        ? err?.message.join(", ")
        : (err?.message ?? `HTTP ${res.status}`),
    );
  }
  const body = (await res.json()) as { data: T };
  return body.data;
}

export const useUserBrandsStore = defineStore("userBrands", () => {
  /** Calling user's own allowlist. Empty = global access. */
  const mine = ref<UserBrand[]>([]);
  /** Allowlists fetched per user id (admin-side cache). */
  const byUser = ref<Record<string, UserBrand[]>>({});
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchMine(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await authFetch("/api/v1/me/brands");
      mine.value = await unwrapData<UserBrand[]>(res);
    } catch (e) {
      error.value = (e as Error).message;
      mine.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function fetchForUser(userId: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await authFetch(`/api/v1/users/${userId}/brands`);
      byUser.value[userId] = await unwrapData<UserBrand[]>(res);
    } catch (e) {
      error.value = (e as Error).message;
    } finally {
      loading.value = false;
    }
  }

  async function setAllowlist(
    userId: string,
    brandCodes: string[],
  ): Promise<UserBrand[]> {
    const res = await authFetch(`/api/v1/users/${userId}/brands`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ brandCodes }),
    });
    const out = await unwrapData<UserBrand[]>(res);
    byUser.value[userId] = out;
    return out;
  }

  return {
    mine,
    byUser,
    loading,
    error,
    fetchMine,
    fetchForUser,
    setAllowlist,
  };
});
